import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import {
  Button,
  Container,
  Box,
  Typography,
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  IconButton,
  useTheme,
  Chip,
  Divider,
  Switch,
  FormControlLabel,
  CircularProgress,
  Slider,
  Modal,
  Snackbar,
  Grid,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  styled,
} from '@mui/material';
import PreviewIcon from '@mui/icons-material/Preview';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  FormatBold,
  FormatItalic,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  Close as CloseIcon,
  Palette as PaletteIcon,
  AutoAwesome as AutoAwesomeIcon,
  Save as SaveIcon,
  RestartAlt as ResetIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  FitScreen as FitScreenIcon,
  PictureInPictureAlt as FloatIcon,
  ViewSidebar as DockIcon,
  DragIndicator as DragIcon,
  GridOn as GridIcon,
  WarningAmber as WarningIcon,
  AspectRatio as AspectIcon,
  Tune as TuneIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import MuiAlert from '@mui/material/Alert';
import { useNavigate } from 'react-router-dom';
import { useBranch } from '../../../layouts/BranchContext';
import { alpha } from '@mui/material/styles';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

/* ── Shared axios instance with token interceptor ────────────────────────── */
const api = axios.create({ baseURL: `${API_BASE_URL}/api` });

const getAccessToken = () => {
  const keys = ['accessToken', 'access_token', 'token'];
  for (const key of keys) {
    const local = localStorage.getItem(key);
    if (local) return local;
    const session = sessionStorage.getItem(key);
    if (session) return session;
  }
  return null;
};

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const getStoredUser = () => {
  try {
    return JSON.parse(sessionStorage.getItem('user') || localStorage.getItem('user') || 'null');
  } catch (e) {
    return null;
  }
};

const getArray = (response) => {
  const data = response?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
const revokeBlob = (url) => {
  if (url && typeof url === 'string' && url.startsWith('blob:')) URL.revokeObjectURL(url);
};

/* ─── Certificate page sizes (standard market / print sizes) ────────────────
   Stored as landscape (long × short) in millimetres.
   Canvas is rendered at 96 dpi so 1 CSS px = 1/96 inch (true print scale).
────────────────────────────────────────────────────────────────────────── */
const MM_TO_PX = 96 / 25.4;

const PAGE_SIZES = {
  A4: { label: 'A4', long: 297, short: 210 },
  A3: { label: 'A3', long: 420, short: 297 },
  A5: { label: 'A5', long: 210, short: 148 },
  Letter: { label: 'US Letter', long: 279.4, short: 215.9 },
  Legal: { label: 'US Legal', long: 355.6, short: 215.9 },
};

const getCanvasSize = (pageSize, orientation) => {
  const p = PAGE_SIZES[pageSize] || PAGE_SIZES.A4;
  const wMm = orientation === 'portrait' ? p.short : p.long;
  const hMm = orientation === 'portrait' ? p.long : p.short;
  return { wMm, hMm, w: Math.round(wMm * MM_TO_PX), h: Math.round(hMm * MM_TO_PX) };
};

/* ─── Default form state ─────────────────────────────────────────────────────── */
const defaultFormState = {
  course: '',
  template_name: '',

  // Page
  page_size: 'A4',
  orientation: 'landscape',

  logo_position: 'top_left',
  institute_name: '',
  institute_name_font_size: 38,
  institute_name_bold: true,
  institute_name_italic: false,
  institute_name_color: '#003366',
  institute_name_alignment: 'center',
  certificate_title: 'Certificate of Completion',
  title_font_size: 36,
  title_bold: true,
  title_italic: false,
  title_color: '#000000',
  title_alignment: 'center',
  body_text: 'This is to certify that {{student_name}} has successfully completed the {{course_name}} on {{completion_date}} under batch {{batch_name}}.',
  body_font_size: 30,
  body_color: '#000000',
  signature_label: '',
  stamp_position: 'right',
  border_style: 'simple',
  border_color: '#000000',

  // Background
  background_type: 'color',
  background_color: '#FFFFFF',
  background_fit: 'cover', // cover | contain | stretch | tile | original
  background_zoom: 100, // %
  background_pos_x: 50, // %
  background_pos_y: 50, // %
  background_opacity: 100, // %

  is_active: true,
  institute_logo: null,
  signature_image: null,
  stamp_image: null,
  background_image: null,
};

const FILE_FIELDS = ['institute_logo', 'signature_image', 'stamp_image', 'background_image'];
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_IMAGE_MB = 10;

const PLACEHOLDERS = [
  { key: '{{student_name}}', label: 'Student Name' },
  { key: '{{course_name}}', label: 'Course Name' },
  { key: '{{completion_date}}', label: 'Completion Date' },
  { key: '{{batch_name}}', label: 'Batch Name' },
];

const LOGO_SLOTS = [
  { value: 'top_left', label: 'Left' },
  { value: 'top_center', label: 'Center' },
  { value: 'top_right', label: 'Right' },
];

const STAMP_SLOTS = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
];

const BORDER_STYLES = ['none', 'simple', 'double', 'decorative'];

const BG_FITS = [
  { value: 'cover', label: 'Fill', hint: 'Covers the whole page. Edges may be cropped.' },
  { value: 'contain', label: 'Fit', hint: 'Shows the whole image. Empty space may appear.' },
  { value: 'stretch', label: 'Stretch', hint: 'Forces the image to the exact page size. May distort.' },
  { value: 'tile', label: 'Tile', hint: 'Repeats the image as a pattern.' },
  { value: 'original', label: 'Original', hint: 'Shows the image at its natural size.' },
];

const ANCHORS = [0, 50, 100];

const PRESET_THEMES = [
  { name: 'Classic Navy', institute: '#003366', title: '#000000', body: '#000000', border: '#003366', background: '#FFFFFF' },
  { name: 'Royal Gold', institute: '#8B6914', title: '#B8860B', body: '#4a4a4a', border: '#B8860B', background: '#FFFBF0' },
  { name: 'Emerald', institute: '#1B5E20', title: '#1B5E20', body: '#2e3b2f', border: '#1B5E20', background: '#F4FFF4' },
  { name: 'Ivory Elegant', institute: '#3e2723', title: '#3e2723', body: '#5d4037', border: '#8d6e63', background: '#FFF8E1' },
  { name: 'Modern Slate', institute: '#37474f', title: '#263238', body: '#455a64', border: '#607d8b', background: '#FFFFFF' },
];

/* Sample data used for the live preview (matches the server preview dummy data) */
const SAMPLE = {
  student_name: 'John Doe',
  course_name: 'Sample Course',
  completion_date: new Date().toLocaleDateString(),
  batch_name: 'Batch A',
};

const fillPlaceholders = (text, sample = SAMPLE) =>
  String(text || '')
    .replace(/\{\{student_name\}\}/g, sample.student_name)
    .replace(/\{\{course_name\}\}/g, sample.course_name)
    .replace(/\{\{completion_date\}\}/g, sample.completion_date)
    .replace(/\{\{batch_name\}\}/g, sample.batch_name);

const getBorderCss = (style, color) => {
  switch (style) {
    case 'double': return `6px double ${color}`;
    case 'decorative': return `10px groove ${color}`;
    case 'simple': return `3px solid ${color}`;
    default: return 'none';
  }
};

/* ─── Build multipart/form-data payload ─────────────────────────────────────── */
function buildFormData(formState) {
  const fd = new FormData();
  Object.entries(formState).forEach(([key, value]) => {
    if (FILE_FIELDS.includes(key)) {
      // Only append if it's a new File object; skip null/string (server keeps existing)
      if (value instanceof File) fd.append(key, value);
    } else if (value !== null && value !== undefined) {
      fd.append(key, value);
    }
  });
  return fd;
}

/* ─── Styled bits ──────────────────────────────────────────────────────────── */
const DropZone = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'hasError',
})(({ theme, active, hasError }) => ({
  border: `2px dashed ${hasError ? theme.palette.error.main : active ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: 12,
  padding: theme.spacing(2),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.25s ease',
  backgroundColor: active
    ? theme.palette.mode === 'dark' ? 'rgba(25,118,210,0.15)' : 'rgba(25,118,210,0.06)'
    : 'transparent',
  '&:hover': {
    borderColor: hasError ? theme.palette.error.main : theme.palette.primary.main,
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
  },
}));

/* Small internal scrollbar helper for the preview modal body */
const CustomPreviewScrollbar = styled('div')(({ theme }) => ({
  overflowY: 'auto',
  maxHeight: 'calc(90vh - 60px)',
  '&::-webkit-scrollbar': { width: '6px', background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  scrollbarWidth: 'thin',
}));

const SectionHeader = ({ icon, children }) => {
  const theme = useTheme();
  return (
    <Typography
      variant="subtitle1"
      component="div"
      sx={{
        fontWeight: 'bold',
        color: theme.palette.primary.main,
        mt: 3,
        mb: 1.5,
        pb: 0.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderBottom: `2px solid ${theme.palette.primary.main}`,
      }}
    >
      {icon}
      {children}
    </Typography>
  );
};

const SlotButton = ({ active, onClick, children }) => {
  const theme = useTheme();
  return (
    <Box
      onClick={onClick}
      sx={{
        px: 2,
        py: 0.5,
        borderRadius: 5,
        border: `1px solid ${active ? theme.palette.primary.main : theme.palette.divider}`,
        backgroundColor: active ? theme.palette.primary.main : 'transparent',
        color: active ? theme.palette.primary.contrastText : theme.palette.text.disabled,
        cursor: 'pointer',
        fontSize: '0.75rem',
        fontWeight: 600,
        transition: 'all 0.2s ease',
        userSelect: 'none',
        '&:hover': { borderColor: theme.palette.primary.main },
      }}
    >
      {children}
    </Box>
  );
};

const ColorField = ({ label, value, onChange }) => {
  const theme = useTheme();
  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 0.5, color: theme.palette.text.disabled }}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <input type="color" value={value} onChange={onChange} style={{ width: 48, height: 36, border: 'none', cursor: 'pointer', borderRadius: 6 }} />
        <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>{value}</Typography>
      </Box>
    </Box>
  );
};

/* ─── Reusable drag & drop upload zone ─────────────────────────────────────── */
const UploadZone = ({ label, preview, onFile, onRemove, height = 56, wide = false, error = false, helperText }) => {
  const theme = useTheme();
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        style={{ display: 'none' }}
        onChange={(e) => {
          onFile(e.target.files && e.target.files[0]);
          e.target.value = ''; // allow re-selecting the same file
        }}
      />
      <DropZone
        active={drag}
        hasError={error}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          onFile(e.dataTransfer.files && e.dataTransfer.files[0]);
        }}
        onClick={() => inputRef.current && inputRef.current.click()}
      >
        {preview ? (
          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <Box
              component="img"
              src={preview}
              alt={label}
              sx={{ height, objectFit: 'contain', maxWidth: wide ? '100%' : 200, borderRadius: 1 }}
            />
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onRemove(); }}
              sx={{ position: 'absolute', top: -8, right: -8, backgroundColor: theme.palette.background.paper, boxShadow: 1 }}
            >
              <HighlightOffIcon fontSize="small" color="error" />
            </IconButton>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, py: 1 }}>
            <UploadFileIcon sx={{ color: error ? theme.palette.error.main : theme.palette.primary.main }} />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>{label}</Typography>
            <Typography variant="caption" color="text.disabled">Drag & drop or click to upload</Typography>
          </Box>
        )}
      </DropZone>
      {helperText && (
        <FormHelperText error={error} sx={{ mx: 1 }}>{helperText}</FormHelperText>
      )}
    </Box>
  );
};

/* ─── Scaled canvas wrapper: renders children at real (96 dpi) size then scales ─
   fit=true → auto-fits the parent's width (used in the server preview modal)
────────────────────────────────────────────────────────────────────────── */
const ScaledCanvas = ({ canvasW, canvasH, scale = 1, fit = false, children }) => {
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!fit || !wrapRef.current) return undefined;
    const el = wrapRef.current;
    const update = () => setWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fit]);

  const s = fit ? (width ? width / canvasW : 0.5) : scale;

  return (
    <Box
      ref={wrapRef}
      sx={{
        width: fit ? '100%' : canvasW * s,
        height: canvasH * s,
        position: 'relative',
        overflow: 'hidden',
        mx: 'auto',
        boxShadow: '0 6px 24px rgba(0,0,0,0.25)',
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          width: canvasW,
          height: canvasH,
          position: 'absolute',
          top: 0,
          left: 0,
          transform: `scale(${s})`,
          transformOrigin: 'top left',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

/* ─── The certificate itself (rendered at exact 96 dpi pixel size) ─────────── */
const CertificateCanvas = ({
  form,
  canvasW,
  canvasH,
  logoSrc,
  sigSrc,
  stampSrc,
  bgSrc,
  sample = SAMPLE,
  overrides = {},
  showGuides = false,
  showHints = true,
}) => {
  const useImage = form.background_type === 'image';
  const fit = form.background_fit || 'cover';
  const zoom = Number(form.background_zoom ?? 100) / 100;
  const px = Number(form.background_pos_x ?? 50);
  const py = Number(form.background_pos_y ?? 50);
  const opacity = Number(form.background_opacity ?? 100) / 100;

  const bgLayerStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `url("${bgSrc}")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: `${px}% ${py}%`,
    opacity,
  };
  switch (fit) {
    case 'contain': bgLayerStyle.backgroundSize = 'contain'; break;
    case 'stretch': bgLayerStyle.backgroundSize = '100% 100%'; break;
    case 'original': bgLayerStyle.backgroundSize = 'auto'; break;
    case 'tile':
      bgLayerStyle.backgroundSize = `${Math.round((canvasW / 3) * zoom)}px auto`;
      bgLayerStyle.backgroundRepeat = 'repeat';
      break;
    default: bgLayerStyle.backgroundSize = 'cover';
  }
  if (fit !== 'tile') {
    bgLayerStyle.transform = `scale(${zoom})`;
    bgLayerStyle.transformOrigin = `${px}% ${py}%`;
  }

  const logoJustify =
    form.logo_position === 'top_left' ? 'flex-start' : form.logo_position === 'top_right' ? 'flex-end' : 'center';

  const stampPos = form.stamp_position;
  const sigPos = stampPos === 'center' ? 'right' : 'center';

  const instituteName = overrides.institute_name || form.institute_name;
  const titleText = overrides.certificate_title || form.certificate_title || 'Certificate';
  const bodyText = overrides.body_text || form.body_text;

  const labelSx = { fontSize: 16, color: form.body_color, mt: 0.5, lineHeight: 1.3 };

  const signature = sigSrc ? (
    <Box sx={{ textAlign: 'center' }}>
      <Box component="img" src={sigSrc} alt="Signature" sx={{ height: 64, objectFit: 'contain', display: 'block', mx: 'auto', mb: 0.5 }} />
      <Box sx={{ width: 190, borderTop: `2px solid ${form.body_color}`, mx: 'auto' }} />
      {form.signature_label && <Typography sx={labelSx}>{form.signature_label}</Typography>}
    </Box>
  ) : form.signature_label ? (
    <Box sx={{ textAlign: 'center' }}>
      <Box sx={{ width: 190, borderTop: `2px solid ${form.body_color}`, mx: 'auto' }} />
      <Typography sx={labelSx}>{form.signature_label}</Typography>
    </Box>
  ) : showHints ? (
    <Typography sx={{ fontSize: 14, color: 'rgba(0,0,0,0.3)' }}>✍ signature</Typography>
  ) : null;

  const stamp = stampSrc ? (
    <Box component="img" src={stampSrc} alt="Stamp" sx={{ height: 96, objectFit: 'contain' }} />
  ) : showHints ? (
    <Typography sx={{ fontSize: 14, color: 'rgba(0,0,0,0.3)' }}>🔩 stamp</Typography>
  ) : null;

  const footerCell = (pos) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', minHeight: 100 }}>
      {stampPos === pos && stamp}
      {sigPos === pos && signature}
    </Box>
  );

  return (
    <Box
      sx={{
        width: canvasW,
        height: canvasH,
        position: 'relative',
        overflow: 'hidden',
        boxSizing: 'border-box',
        backgroundColor: useImage ? form.background_color : form.background_color,
        color: form.body_color,
        textAlign: 'center',
      }}
    >
      {/* Background image layer */}
      {useImage && bgSrc && <div style={bgLayerStyle} />}

      {/* Border frame (inset like printed certificates) */}
      {form.border_style !== 'none' && (
        <Box
          sx={{
            position: 'absolute',
            inset: 22,
            border: getBorderCss(form.border_style, form.border_color),
            borderRadius: '4px',
            pointerEvents: 'none',
            zIndex: 2,
          }}
        />
      )}

      {/* Missing background hint */}
      {showHints && useImage && !bgSrc && (
        <Box
          sx={{
            position: 'absolute',
            top: 40,
            right: 40,
            zIndex: 6,
            px: 2,
            py: 0.5,
            borderRadius: 2,
            backgroundColor: 'rgba(211,47,47,0.9)',
            color: '#fff',
            fontSize: 16,
            fontWeight: 600,
          }}
        >
          ⚠ No background image uploaded
        </Box>
      )}

      {/* Content */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 3,
          height: '100%',
          boxSizing: 'border-box',
          p: '58px 70px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header group */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: logoJustify, alignItems: 'center', minHeight: 88, mb: 1 }}>
            {logoSrc ? (
              <Box component="img" src={logoSrc} alt="Logo" sx={{ height: 84, objectFit: 'contain' }} />
            ) : showHints ? (
              <Typography sx={{ fontSize: 14, color: 'rgba(0,0,0,0.3)' }}>🖼 drop logo</Typography>
            ) : null}
          </Box>

          {instituteName && (
            <Typography
              sx={{
                fontSize: form.institute_name_font_size,
                fontWeight: form.institute_name_bold ? 'bold' : 'normal',
                fontStyle: form.institute_name_italic ? 'italic' : 'normal',
                color: form.institute_name_color,
                textAlign: form.institute_name_alignment,
                lineHeight: 1.25,
                mb: 1.5,
              }}
            >
              {instituteName}
            </Typography>
          )}

          <Typography
            sx={{
              fontSize: form.title_font_size,
              fontWeight: form.title_bold ? 'bold' : 'normal',
              fontStyle: form.title_italic ? 'italic' : 'normal',
              color: form.title_color,
              textAlign: form.title_alignment,
              lineHeight: 1.2,
            }}
          >
            {titleText}
          </Typography>
        </Box>

        {/* Body */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 4 }}>
          <Typography
            sx={{
              fontSize: form.body_font_size,
              color: form.body_color,
              lineHeight: 1.7,
              whiteSpace: 'pre-line',
              textAlign: 'center',
            }}
          >
            {fillPlaceholders(bodyText, sample)}
          </Typography>
        </Box>

        {/* Footer: signature + stamp */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', alignItems: 'end', gap: 2 }}>
          {footerCell('left')}
          {footerCell('center')}
          {footerCell('right')}
        </Box>
      </Box>

      {/* Guides overlay */}
      {showGuides && (
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 8, pointerEvents: 'none' }}>
          <Box sx={{ position: 'absolute', inset: 48, border: '1px dashed rgba(233,30,99,0.7)' }} />
          <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, borderLeft: '1px dashed rgba(33,150,243,0.7)' }} />
          <Box sx={{ position: 'absolute', top: '50%', left: 0, right: 0, borderTop: '1px dashed rgba(33,150,243,0.7)' }} />
        </Box>
      )}
    </Box>
  );
};

const MIN_SCALE = 0.2;
const MAX_SCALE = 1.6;

/* ═══════════════════════════════════════════════════════════════════════════
   CERTIFICATE DESIGNER — full-page, Canva-style
   mode="add"  → POST /api/admission/certificate-template/  (+ overwrite flow)
   mode="edit" → PUT  /api/admission/certificate-template/<id>/
   Preview:     POST /api/admission/certificate-template/preview/

   Organization comes from the stored user (session storage).
   Branch comes from the global branch filter (BranchContext) in add mode;
   in edit mode the template's already-saved organization/branch are used
   by default (captured from the GET response) so updates don't move the
   template to a different branch by accident.
═══════════════════════════════════════════════════════════════════════════ */
export const CertificateDesigner = ({ mode = 'add', certificateId = null }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isEdit = mode === 'edit';

  /* ── Global branch from BranchContext ── */
  const { selectedBranchId } = useBranch();

  const [form, setForm] = useState({ ...defaultFormState });
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showOverwriteModal, setShowOverwriteModal] = useState(false);
  const [overwriteInfo, setOverwriteInfo] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [showErrors, setShowErrors] = useState(false);

  /* ── Organization from stored user ── */
  const [organizationId, setOrganizationId] = useState('');

  /* ── Edit mode: the template's own saved org/branch (used by default in PUT) ── */
  const [savedOrgId, setSavedOrgId] = useState(null);
  const [savedBranchId, setSavedBranchId] = useState(null);

  useEffect(() => {
    const user = getStoredUser();
    const orgId = user?.organization_id || user?.organization?.id || user?.organization || '';
    setOrganizationId(orgId ? String(orgId) : '');
  }, []);

  /* ── Image previews (object URLs for new uploads, server URLs in edit) ── */
  const [logoPrev, setLogoPrev] = useState(null);
  const [sigPrev, setSigPrev] = useState(null);
  const [stampPrev, setStampPrev] = useState(null);
  const [bgPrev, setBgPrev] = useState(null);
  const [bgDims, setBgDims] = useState(null);

  /* ── Live preview panel state ── */
  const [previewMode, setPreviewMode] = useState('floating'); // 'docked' | 'floating'
  const [previewScale, setPreviewScale] = useState(0.3);      // 30%
  const [autoFit, setAutoFit] = useState(false);
  const [previewCollapsed, setPreviewCollapsed] = useState(false);
  const [showGuides, setShowGuides] = useState(false);
  const [floatPos, setFloatPos] = useState({ x: 16, y: 150 }); // top-left

  const bodyTextRef = useRef(null);
  const dockViewportRef = useRef(null);
  const floatPanelRef = useRef(null);
  const basicRef = useRef(null);
  const bgSectionRef = useRef(null);

  const canvas = useMemo(() => getCanvasSize(form.page_size, form.orientation), [form.page_size, form.orientation]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleAuthError = (error) => {
    if (error.response?.status === 401) {
      ['accessToken', 'access_token', 'token'].forEach((k) => {
        localStorage.removeItem(k);
        sessionStorage.removeItem(k);
      });
      navigate('/login');
    }
  };

  /* ── Courses — branch-scoped ── */
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const params = {};
        if (organizationId) params.organization = Number(organizationId);
        if (selectedBranchId) params.branch = Number(selectedBranchId);
        const response = await api.get('/course/courses/', { params });
        setCourses(getArray(response));
      } catch (error) {
        console.error('Error fetching courses:', error);
        showSnackbar('Failed to fetch courses', 'error');
        handleAuthError(error);
      }
    };
    fetchCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, selectedBranchId]);

  /* ── Edit mode: load template via GET /admission/certificate-template/<id>/ ── */
  useEffect(() => {
    if (!isEdit || !certificateId) return;
    const fetchTemplate = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/admission/certificate-template/${certificateId}/`);
        const d = response.data;
        // Capture the template's own org/branch — these are sent by default on PUT
        setSavedOrgId(d.organization ?? null);
        setSavedBranchId(d.branch ?? null);
        setForm({
          course: d.course ?? '',
          template_name: d.template_name ?? '',
          page_size: PAGE_SIZES[d.page_size] ? d.page_size : 'A4',
          orientation: d.orientation === 'portrait' ? 'portrait' : 'landscape',
          logo_position: d.logo_position ?? 'top_left',
          institute_name: d.institute_name ?? '',
          institute_name_font_size: d.institute_name_font_size ?? 30,
          institute_name_bold: d.institute_name_bold ?? true,
          institute_name_italic: d.institute_name_italic ?? false,
          institute_name_color: d.institute_name_color ?? '#003366',
          institute_name_alignment: d.institute_name_alignment ?? 'center',
          certificate_title: d.certificate_title ?? 'Certificate of Completion',
          title_font_size: d.title_font_size ?? 36,
          title_bold: d.title_bold ?? true,
          title_italic: d.title_italic ?? false,
          title_color: d.title_color ?? '#000000',
          title_alignment: d.title_alignment ?? 'center',
          body_text: d.body_text ?? defaultFormState.body_text,
          body_font_size: d.body_font_size ?? 30,
          body_color: d.body_color ?? '#000000',
          signature_label: d.signature_label ?? '',
          stamp_position: d.stamp_position ?? 'right',
          border_style: d.border_style ?? 'simple',
          border_color: d.border_color ?? '#000000',
          background_type: d.background_type ?? 'color',
          background_color: d.background_color ?? '#FFFFFF',
          background_fit: BG_FITS.some((f) => f.value === d.background_fit) ? d.background_fit : 'cover',
          background_zoom: Number(d.background_zoom ?? 100),
          background_pos_x: Number(d.background_pos_x ?? 50),
          background_pos_y: Number(d.background_pos_y ?? 50),
          background_opacity: Number(d.background_opacity ?? 100),
          is_active: d.is_active ?? true,
          // File fields: null = keep existing on server; only set if user uploads new file
          institute_logo: null,
          signature_image: null,
          stamp_image: null,
          background_image: null,
        });
        setLogoPrev(d.institute_logo_url || d.institute_logo || null);
        setSigPrev(d.signature_image_url || d.signature_image || null);
        setStampPrev(d.stamp_image_url || d.stamp_image || null);
        setBgPrev(d.background_image_url || d.background_image || null);
      } catch (error) {
        console.error('Error fetching template:', error);
        showSnackbar('Failed to load certificate template', 'error');
        handleAuthError(error);
      } finally {
        setLoading(false);
      }
    };
    fetchTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, certificateId]);

  /* ── Read natural size of the background image (for fit hints / DPI) ── */
  useEffect(() => {
    if (!bgPrev) {
      setBgDims(null);
      return undefined;
    }
    let cancelled = false;
    const img = new Image();
    img.onload = () => { if (!cancelled) setBgDims({ w: img.naturalWidth, h: img.naturalHeight }); };
    img.onerror = () => { if (!cancelled) setBgDims(null); };
    img.src = bgPrev;
    return () => { cancelled = true; };
  }, [bgPrev]);

  /* ── Docked preview: auto-fit to the available width ── */
  useEffect(() => {
    if (loading || previewMode !== 'docked' || !autoFit || previewCollapsed) return undefined;
    const el = dockViewportRef.current;
    if (!el) return undefined;
    const apply = () => setPreviewScale(clamp((el.clientWidth - 24) / canvas.w, MIN_SCALE, MAX_SCALE));
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, previewMode, autoFit, previewCollapsed, canvas.w]);

  /* ── Keep floating panel inside the viewport on window resize ── */
  useEffect(() => {
    if (previewMode !== 'floating') return undefined;
    const onResize = () =>
      setFloatPos((p) => ({
        x: clamp(p.x, 0, Math.max(0, window.innerWidth - 160)),
        y: clamp(p.y, 0, Math.max(0, window.innerHeight - 56)),
      }));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [previewMode]);

  /* ── Field helpers ── */
  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const previewMap = {
    institute_logo: [logoPrev, setLogoPrev],
    signature_image: [sigPrev, setSigPrev],
    stamp_image: [stampPrev, setStampPrev],
    background_image: [bgPrev, setBgPrev],
  };

  /* Pick the best fit automatically for a freshly uploaded / existing background */
  const autoFitBackground = useCallback((url, announce = true) => {
    if (!url) return;
    const img = new Image();
    img.onload = () => {
      const imgAspect = img.naturalWidth / img.naturalHeight;
      const pageAspect = canvas.w / canvas.h;
      const diff = Math.abs(imgAspect - pageAspect) / pageAspect;
      const fitMode = diff < 0.03 ? 'stretch' : 'cover';
      setForm((prev) => ({
        ...prev,
        background_fit: fitMode,
        background_zoom: 100,
        background_pos_x: 50,
        background_pos_y: 50,
      }));
      if (announce) {
        showSnackbar(
          fitMode === 'stretch'
            ? 'Image matches the page ratio — fitted perfectly'
            : 'Auto-fitted using "Fill". Adjust Fit / Zoom / Position if needed.'
        );
      }
    };
    img.src = url;
  }, [canvas.w, canvas.h]);

  const handleFile = (field, file) => {
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      showSnackbar('Please upload a JPG, PNG or WebP image', 'error');
      return;
    }
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      showSnackbar(`Image is too large (max ${MAX_IMAGE_MB} MB)`, 'error');
      return;
    }
    const [current, setter] = previewMap[field];
    revokeBlob(current);
    const url = URL.createObjectURL(file);
    setter(url);
    setField(field, file);
    if (field === 'background_image') autoFitBackground(url, true);
  };

  const removeImage = (field) => {
    const [current, setter] = previewMap[field];
    revokeBlob(current);
    setter(null);
    setField(field, null);
  };

  const insertPlaceholder = (key) => {
    const el = bodyTextRef.current;
    if (el && el.selectionStart != null) {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const text = form.body_text.slice(0, start) + key + form.body_text.slice(end);
      setField('body_text', text);
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(start + key.length, start + key.length);
      });
    } else {
      setField('body_text', form.body_text + key);
    }
  };

  const applyThemePreset = (preset) => {
    setForm((prev) => ({
      ...prev,
      institute_name_color: preset.institute,
      title_color: preset.title,
      body_color: preset.body,
      border_color: preset.border,
      background_color: preset.background,
    }));
    showSnackbar(`"${preset.name}" theme applied 🎨`);
  };

  const resetBackgroundAdjustments = () => {
    setForm((prev) => ({
      ...prev,
      background_fit: 'cover',
      background_zoom: 100,
      background_pos_x: 50,
      background_pos_y: 50,
      background_opacity: 100,
    }));
  };

  /* ── Validation ── */
  const hasBgImage = Boolean(bgPrev);

  const errors = useMemo(() => {
    const e = {};
    if (!form.course) e.course = 'Please select a course';
    if (!String(form.template_name || '').trim()) e.template_name = 'Template name is required';
    if (form.background_type === 'image' && !hasBgImage) {
      e.background_image = 'Background type is "Image" but no image is uploaded. Upload one or switch to "Color".';
    }
    return e;
  }, [form.course, form.template_name, form.background_type, hasBgImage]);

  const errorList = Object.values(errors);
  const hasErrors = errorList.length > 0;

  const guardValidation = () => {
    if (!hasErrors) return true;
    setShowErrors(true);
    showSnackbar(errorList[0], 'error');
    const target = errors.course || errors.template_name ? basicRef.current : bgSectionRef.current;
    if (target && target.scrollIntoView) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return false;
  };

  /* ── Resolve org/branch for save payloads ──
     Add mode:  organization ← stored user, branch ← global branch filter
     Edit mode: by default the template's already-saved organization/branch
                (captured from the GET response) are sent; if the saved template
                has none, we fall back to the global values. */
  const resolveOrgBranch = () => {
    const org = isEdit && savedOrgId != null && savedOrgId !== ''
      ? savedOrgId
      : organizationId;
    const branch = isEdit && savedBranchId != null && savedBranchId !== ''
      ? savedBranchId
      : selectedBranchId;
    return { org, branch };
  };

  /* ── PREVIEW: POST /api/admission/certificate-template/preview/ ── */
  const handlePreview = async () => {
    if (!guardValidation()) return;
    try {
      setPreviewing(true);
      const payload = buildFormData(form);
      const response = await api.post('/admission/certificate-template/preview/', payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data?.success) {
        setPreviewData(response.data.preview);
        setShowPreviewModal(true);
      } else if (response.data) {
        setPreviewData(response.data.preview || response.data);
        setShowPreviewModal(true);
      }
    } catch (error) {
      console.error('Preview error:', error);
      showSnackbar('Failed to generate preview', 'error');
      handleAuthError(error);
    } finally {
      setPreviewing(false);
    }
  };

  /* ── SAVE ── */
  // Add:  POST (form-data) /api/admission/certificate-template/
  //       payload includes organization (stored user) + branch (global filter)
  //       201 created | overwrite_required: { overwrite_required: true, existing_template_id, message }
  // Edit: PUT (form-data) /api/admission/certificate-template/<id>/
  //       payload includes the template's already-saved organization/branch by default
  const saveTemplate = async (overwrite = false, existingId = null) => {
    if (hasErrors) {
      showSnackbar(errorList[0], 'error');
      return { success: false };
    }
    try {
      const payload = buildFormData(form);

      // ── Global branch filter wiring ──
      const { org, branch } = resolveOrgBranch();
      if (org !== '' && org != null) payload.append('organization', Number(org));
      if (branch) payload.append('branch', Number(branch));

      const config = { headers: { 'Content-Type': 'multipart/form-data' } };

      if (isEdit && certificateId) {
        const response = await api.put(`/admission/certificate-template/${certificateId}/`, payload, config);
        if (response.status === 200) {
          showSnackbar('Certificate template updated successfully!');
          return { success: true };
        }
      } else if (overwrite && existingId) {
        const response = await api.put(`/admission/certificate-template/${existingId}/`, payload, config);
        if (response.status === 200) {
          showSnackbar('Certificate template overwritten successfully!');
          return { success: true };
        }
      } else {
        const response = await api.post('/admission/certificate-template/', payload, config);
        if (response.status === 201) {
          showSnackbar('Certificate template added successfully!');
          return { success: true };
        }
        // overwrite_required returned as 2xx (some backends)
        if (response.data?.overwrite_required) {
          return {
            overwrite_required: true,
            existing_template_id: response.data.existing_template_id,
            message: response.data.message,
          };
        }
      }
      return { success: true };
    } catch (error) {
      if (error.response?.data?.overwrite_required) {
        return {
          overwrite_required: true,
          existing_template_id: error.response.data.existing_template_id,
          message: error.response.data.message,
        };
      }
      console.error('Error saving certificate:', error);
      showSnackbar(error.response?.data?.message || 'Failed to save certificate template', 'error');
      handleAuthError(error);
      return { success: false };
    }
  };

  const handleSave = async () => {
    if (!guardValidation()) return;
    setSaving(true);
    const result = await saveTemplate();
    setSaving(false);

    if (result?.overwrite_required) {
      setOverwriteInfo(result);
      setShowOverwriteModal(true);
    } else if (result?.success) {
      navigate('/certificate');
    }
  };

  const handleOverwriteConfirm = async () => {
    setShowOverwriteModal(false);
    setSaving(true);
    const result = await saveTemplate(true, overwriteInfo.existing_template_id);
    setSaving(false);
    setOverwriteInfo(null);
    if (result?.success) navigate('/certificate');
  };

  const handleCancel = () => navigate('/certificate');

  const resetForm = () => {
    [logoPrev, sigPrev, stampPrev, bgPrev].forEach(revokeBlob);
    setForm({ ...defaultFormState });
    setLogoPrev(null);
    setSigPrev(null);
    setStampPrev(null);
    setBgPrev(null);
    setPreviewData(null);
    setShowErrors(false);
  };

  const handleReset = () => {
    const confirmed = window.confirm('Are you sure? All your design changes will be lost.');
    if (confirmed) resetForm();
  };

  /* ── Preview panel controls ── */
  const setZoom = (v) => {
    setAutoFit(false);
    setPreviewScale(clamp(v, MIN_SCALE, MAX_SCALE));
  };
  const zoomBy = (delta) => setZoom(Math.round((previewScale + delta) * 100) / 100);

  const fitPreview = () => {
    if (previewMode === 'docked') {
      setAutoFit(true);
    } else {
      setAutoFit(false);
      const targetW = Math.min(window.innerWidth * 0.45, 720);
      setPreviewScale(clamp(targetW / canvas.w, MIN_SCALE, MAX_SCALE));
    }
  };

  const changeMode = (nextMode) => {
    if (nextMode === previewMode) return;
    setPreviewMode(nextMode);
    setPreviewCollapsed(false);
    if (nextMode === 'floating') {
      setAutoFit(false);
      const targetW = Math.min(window.innerWidth * 0.4, 640);
      const s = clamp(targetW / canvas.w, MIN_SCALE, MAX_SCALE);
      setPreviewScale(s);
      setFloatPos({ x: Math.max(12, window.innerWidth - (canvas.w * s + 24) - 24), y: 84 });
    } else {
      setAutoFit(true);
    }
  };

  const startDrag = (e) => {
    if (previewMode !== 'floating') return;
    if (e.target.closest && e.target.closest('button')) return;
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const orig = { ...floatPos };
    const move = (ev) => {
      setFloatPos({
        x: clamp(orig.x + ev.clientX - startX, 0, Math.max(0, window.innerWidth - 160)),
        y: clamp(orig.y + ev.clientY - startY, 0, Math.max(0, window.innerHeight - 56)),
      });
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const startResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = canvas.w * previewScale;
    const move = (ev) => {
      setAutoFit(false);
      setPreviewScale(clamp((startW + ev.clientX - startX) / canvas.w, MIN_SCALE, MAX_SCALE));
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const snapTo = (corner) => {
    const el = floatPanelRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const m = 16;
    const x = corner.includes('l') ? m : window.innerWidth - r.width - m;
    const y = corner.includes('t') ? 72 : window.innerHeight - r.height - m;
    setFloatPos({ x: Math.max(0, x), y: Math.max(0, y) });
  };

  /* ── Reusable preview pieces (called as functions, NOT components, to avoid remounting) ── */
  const renderCanvas = (scale) => (
    <ScaledCanvas canvasW={canvas.w} canvasH={canvas.h} scale={scale}>
      <CertificateCanvas
        form={form}
        canvasW={canvas.w}
        canvasH={canvas.h}
        logoSrc={logoPrev}
        sigSrc={sigPrev}
        stampSrc={stampPrev}
        bgSrc={bgPrev}
        showGuides={showGuides}
      />
    </ScaledCanvas>
  );

  const renderPreviewHeader = (floating) => (
    <Box
      onPointerDown={floating ? startDrag : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1.5,
        py: 0.75,
        backgroundColor: theme.palette.primary.main,
        color: '#fff',
        cursor: floating ? 'grab' : 'default',
        touchAction: 'none',
        userSelect: 'none',
        '&:active': { cursor: floating ? 'grabbing' : 'default' },
      }}
    >
      {floating && <DragIcon fontSize="small" />}
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
        🔮 Live Preview
      </Typography>
      <Chip
        size="small"
        label={`${(PAGE_SIZES[form.page_size] || PAGE_SIZES.A4).label} · ${form.orientation}`}
        sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)', height: 22 }}
        variant="outlined"
      />
      <Tooltip title="Dock beside the editor">
        <IconButton size="small" onClick={() => changeMode('docked')} sx={{ color: '#fff', opacity: previewMode === 'docked' ? 1 : 0.6 }}>
          <DockIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Float (drag anywhere)">
        <IconButton size="small" onClick={() => changeMode('floating')} sx={{ color: '#fff', opacity: previewMode === 'floating' ? 1 : 0.6 }}>
          <FloatIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={previewCollapsed ? 'Expand' : 'Collapse'}>
        <IconButton size="small" onClick={() => setPreviewCollapsed((v) => !v)} sx={{ color: '#fff' }}>
          {previewCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
    </Box>
  );

  const renderPreviewToolbar = (floating) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', px: 1.5, py: 0.75 }}>
      <Tooltip title="Zoom out">
        <IconButton size="small" onClick={() => zoomBy(-0.05)}><ZoomOutIcon fontSize="small" /></IconButton>
      </Tooltip>
      <Slider
        size="small"
        sx={{ width: 110, mx: 1 }}
        min={Math.round(MIN_SCALE * 100)}
        max={Math.round(MAX_SCALE * 100)}
        value={Math.round(previewScale * 100)}
        onChange={(_, v) => setZoom(v / 100)}
      />
      <Tooltip title="Zoom in">
        <IconButton size="small" onClick={() => zoomBy(0.05)}><ZoomInIcon fontSize="small" /></IconButton>
      </Tooltip>
      <Chip size="small" label={`${Math.round(previewScale * 100)}%`} sx={{ minWidth: 52 }} />
      <Tooltip title="Fit to available space">
        <Button size="small" startIcon={<FitScreenIcon />} onClick={fitPreview} sx={{ minWidth: 0 }}>Fit</Button>
      </Tooltip>
      <Tooltip title="Actual print size at 96 dpi">
        <Button size="small" onClick={() => setZoom(1)} sx={{ minWidth: 0 }}>1:1</Button>
      </Tooltip>
      <Tooltip title="Toggle guides (margins & center lines)">
        <ToggleButton
          size="small"
          value="guides"
          selected={showGuides}
          onChange={() => setShowGuides((v) => !v)}
          sx={{ p: 0.5, border: 'none' }}
        >
          <GridIcon fontSize="small" />
        </ToggleButton>
      </Tooltip>
      {floating && (
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', gap: 0.25 }}>
          <Typography variant="caption" color="text.disabled" sx={{ mr: 0.5 }}>Snap</Typography>
          {[['tl', '↖'], ['tr', '↗'], ['bl', '↙'], ['br', '↘']].map(([c, sym]) => (
            <IconButton key={c} size="small" onClick={() => snapTo(c)} sx={{ width: 24, height: 24, fontSize: 14 }}>
              {sym}
            </IconButton>
          ))}
        </Box>
      )}
    </Box>
  );

  const pageInfo = `${(PAGE_SIZES[form.page_size] || PAGE_SIZES.A4).label} ${form.orientation} · ${canvas.wMm} × ${canvas.hMm} mm`;

  /* ── Background image quality / ratio info ── */
  const bgInfo = useMemo(() => {
    if (!bgDims) return null;
    const imgAspect = bgDims.w / bgDims.h;
    const pageAspect = canvas.w / canvas.h;
    const diff = Math.abs(imgAspect - pageAspect) / pageAspect;
    const dpi = Math.round(bgDims.w / (canvas.w / 96));
    const quality = dpi >= 250 ? { label: 'Excellent (print-ready)', color: 'success' } : dpi >= 150 ? { label: 'Good', color: 'info' } : { label: 'Low — may look blurry in print', color: 'warning' };
    return { imgAspect, pageAspect, diff, dpi, quality };
  }, [bgDims, canvas.w, canvas.h]);

  const isImageBg = form.background_type === 'image';
  const bgHasError = showErrors && Boolean(errors.background_image);

  /* ── Server-preview sample values ── */
  const serverSample = {
    student_name: previewData?.student_name || SAMPLE.student_name,
    course_name: previewData?.course_name || SAMPLE.course_name,
    completion_date: previewData?.completion_date || SAMPLE.completion_date,
    batch_name: previewData?.batch_name || SAMPLE.batch_name,
  };

  const docked = previewMode === 'docked';

  const fieldSx = {
  // Labels (TextField, Select)
  '&& .MuiInputLabel-root': {
    color: theme.palette.text.primary,
  },
  '&& .MuiInputLabel-root.Mui-focused': {
    color: theme.palette.primary.main,
  },
  '&& .MuiInputLabel-root.Mui-error': {
    color: theme.palette.error.main,
  },

  // Helper texts
  '&& .MuiFormHelperText-root': {
    color: theme.palette.text.primary,
    opacity: 0.85,
  },
  '&& .MuiFormHelperText-root.Mui-error': {
    color: theme.palette.error.main,
    opacity: 1,
  },

  // Borders
  '&& .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha(theme.palette.text.primary, 0.4),
  },
  '&& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
  '&& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
  '&& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.error.main,
  },

  // Typed text and dropdown arrow
  '&& .MuiOutlinedInput-input, && .MuiSelect-select, && textarea': {
    color: theme.palette.text.primary,
  },
  '&& .MuiSelect-icon': {
    color: theme.palette.text.primary,
  },
};

  return (
    <Container maxWidth={false} sx={{ width: '100%', pb: 6 }}>
      {/* ── Page header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            {isEdit ? '✏️ Edit Certificate Template' : '🎨 Add Certificate Template'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
            <Chip size="small" label={isEdit ? `Template #${certificateId}` : 'New Template'} color="primary" variant="outlined" />
            <Chip size="small" icon={<AspectIcon />} label={pageInfo} variant="outlined" />
            <Typography variant="caption" color="text.disabled">
              Design live — every change updates the preview instantly ✨
            </Typography>
          </Box>
        </Box>
                        <Button variant="outlined" color="secondary" onClick={handleCancel}  sx={{ height: 46, borderRadius: '10px', px: 4, fontWeight: 'bold' }}>Back</Button>

        {showErrors && hasErrors && (
          <Chip color="error" icon={<WarningIcon />} label={`${errorList.length} issue${errorList.length > 1 ? 's' : ''} to fix`} />
        )}
        <Tooltip title={hasErrors ? errorList.join(' • ') : isEdit ? 'Update template' : 'Save template'}>
          <span>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              sx={{ height: 46, borderRadius: '10px', px: 4, fontWeight: 'bold', backgroundColor: theme.palette.primary.main, '&:hover': { backgroundColor: theme.palette.primary.dark } }}
            >
              {isEdit ? 'UPDATE' : 'SAVE'}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* ── Validation summary ── */}
      {showErrors && hasErrors && (
        <Box sx={{ px: 2, mb: 2 }}>
          <MuiAlert severity="error" variant="outlined" onClose={() => setShowErrors(false)}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Please fix the following before saving:</Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {errorList.map((msg) => (<li key={msg}>{msg}</li>))}
            </Box>
          </MuiAlert>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading template…</Typography>
        </Box>
      ) : (
        <Grid container spacing={3} sx={{ px: 2 }} alignItems="flex-start" justifyContent={docked ? 'flex-start' : 'center'}>
          {/* ══ LEFT: Designer controls ══ */}
          <Grid item xs={12} md={docked ? 6 : 8} lg={docked ? 5 : 6}>
            <Paper elevation={3} sx={{ borderRadius: '16px', p: 3, ...fieldSx }}>
              {/* ── Basic Info ── */}
              <Box ref={basicRef}>
                <SectionHeader icon={<AutoAwesomeIcon fontSize="small" />}>Basic Information</SectionHeader>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 1 }}>
                  <FormControl fullWidth error={showErrors && Boolean(errors.course)}>
                    <InputLabel sx={{ color: theme.palette.text.primary }} id="course-label">Course *</InputLabel>
                    <Select
                      labelId="course-label"
                      value={form.course}
                      onChange={(e) => setField('course', e.target.value)}
                      label="Course *"
                    >
                      {courses.length === 0 && <MenuItem disabled value=""><em>No courses available</em></MenuItem>}
                      {courses.map((c) => (
                        <MenuItem key={c.id} value={c.id}>{c.course_name || `Course ${c.id}`}</MenuItem>
                      ))}
                    </Select>
                    {showErrors && errors.course && <FormHelperText>{errors.course}</FormHelperText>}
                  </FormControl>
                  <TextField
                    label="Template Name *"
                    variant="outlined"
                    fullWidth
                    value={form.template_name}
                    onChange={(e) => setField('template_name', e.target.value)}
                    error={showErrors && Boolean(errors.template_name)}
                    helperText={showErrors ? errors.template_name : ''}
                  />
                </Box>
                <FormControlLabel
                  control={<Switch checked={form.is_active} onChange={(e) => setField('is_active', e.target.checked)} color="primary" />}
                  label="Active"
                  sx={{ mb: 1 }}
                />
              </Box>

              {/* ── Page size ── */}
              <SectionHeader icon={<AspectIcon fontSize="small" />}>Page Size & Orientation</SectionHeader>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 1 }}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: theme.palette.text.primary }} id="page-size-label">Page Size</InputLabel>
                  <Select labelId="page-size-label" value={form.page_size} label="Page Size" onChange={(e) => setField('page_size', e.target.value)}>
                    {Object.entries(PAGE_SIZES).map(([key, p]) => (
                      <MenuItem key={key} value={key}>{p.label} ({p.long} × {p.short} mm)</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  value={form.orientation}
                  onChange={(_, v) => v && setField('orientation', v)}
                >
                  <ToggleButton value="landscape">Landscape</ToggleButton>
                  <ToggleButton value="portrait">Portrait</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                Exact print size: {canvas.wMm} × {canvas.hMm} mm · {canvas.w} × {canvas.h}px @ 96 dpi ·{' '}
                {Math.round((canvas.wMm / 25.4) * 300)} × {Math.round((canvas.hMm / 25.4) * 300)}px @ 300 dpi.
                A4 landscape is the most common certificate size.
              </Typography>

              {/* ── Theme presets ── */}
              <SectionHeader icon={<PaletteIcon fontSize="small" />}>Theme Presets</SectionHeader>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {PRESET_THEMES.map((preset) => (
                  <Tooltip key={preset.name} title={`Apply "${preset.name}"`}>
                    <Chip
                      label={preset.name}
                      onClick={() => applyThemePreset(preset)}
                      sx={{
                        cursor: 'pointer',
                        border: `2px solid ${preset.border}`,
                        backgroundColor: preset.background,
                        color: preset.institute,
                        fontWeight: 600,
                        '&:hover': { transform: 'scale(1.05)', backgroundColor: preset.background },
                        transition: 'all 0.2s ease',
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>

              {/* ── Logo ── */}
              <SectionHeader icon={<span>🖼</span>}>Institute Logo</SectionHeader>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 1 }}>
                <UploadZone
                  label="Upload Logo (JPG/PNG)"
                  preview={logoPrev}
                  onFile={(f) => handleFile('institute_logo', f)}
                  onRemove={() => removeImage('institute_logo')}
                />
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5, color: theme.palette.text.disabled }}>Logo Position</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {LOGO_SLOTS.map((slot) => (
                      <SlotButton
                        key={slot.value}
                        active={form.logo_position === slot.value}
                        onClick={() => setField('logo_position', slot.value)}
                      >
                        {slot.label}
                      </SlotButton>
                    ))}
                  </Box>
                </Box>
              </Box>

              {/* ── Institute Name ── */}
              <SectionHeader icon={<span>🏫</span>}>Institute Name</SectionHeader>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 1 }}>
                <TextField label="Institute Name" variant="outlined" fullWidth value={form.institute_name} onChange={(e) => setField('institute_name', e.target.value)} />
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5, color: theme.palette.text.disabled }}>Font Size ({form.institute_name_font_size}px)</Typography>
                  <Slider value={form.institute_name_font_size} onChange={(_, v) => setField('institute_name_font_size', v)} min={8} max={72} step={1} valueLabelDisplay="auto" />
                </Box>
                <ColorField label="Text Color" value={form.institute_name_color} onChange={(e) => setField('institute_name_color', e.target.value)} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <ToggleButtonGroup size="small" value={form.institute_name_alignment} exclusive onChange={(_, v) => v && setField('institute_name_alignment', v)}>
                    <ToggleButton value="left"><FormatAlignLeft /></ToggleButton>
                    <ToggleButton value="center"><FormatAlignCenter /></ToggleButton>
                    <ToggleButton value="right"><FormatAlignRight /></ToggleButton>
                  </ToggleButtonGroup>
                  <ToggleButtonGroup size="small">
                    <ToggleButton value="bold" selected={form.institute_name_bold} onClick={() => setField('institute_name_bold', !form.institute_name_bold)}><FormatBold /></ToggleButton>
                    <ToggleButton value="italic" selected={form.institute_name_italic} onClick={() => setField('institute_name_italic', !form.institute_name_italic)}><FormatItalic /></ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Box>

              {/* ── Certificate Title ── */}
              <SectionHeader icon={<span>📜</span>}>Certificate Title</SectionHeader>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 1 }}>
                <TextField label="Certificate Title" variant="outlined" fullWidth value={form.certificate_title} onChange={(e) => setField('certificate_title', e.target.value)} />
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5, color: theme.palette.text.disabled }}>Font Size ({form.title_font_size}px)</Typography>
                  <Slider value={form.title_font_size} onChange={(_, v) => setField('title_font_size', v)} min={8} max={96} step={1} valueLabelDisplay="auto" />
                </Box>
                <ColorField label="Title Color" value={form.title_color} onChange={(e) => setField('title_color', e.target.value)} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <ToggleButtonGroup size="small" value={form.title_alignment} exclusive onChange={(_, v) => v && setField('title_alignment', v)}>
                    <ToggleButton value="left"><FormatAlignLeft /></ToggleButton>
                    <ToggleButton value="center"><FormatAlignCenter /></ToggleButton>
                    <ToggleButton value="right"><FormatAlignRight /></ToggleButton>
                  </ToggleButtonGroup>
                  <ToggleButtonGroup size="small">
                    <ToggleButton value="bold" selected={form.title_bold} onClick={() => setField('title_bold', !form.title_bold)}><FormatBold /></ToggleButton>
                    <ToggleButton value="italic" selected={form.title_italic} onClick={() => setField('title_italic', !form.title_italic)}><FormatItalic /></ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Box>

              {/* ── Body Text ── */}
              <SectionHeader icon={<span>📝</span>}>Body Text</SectionHeader>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                {PLACEHOLDERS.map((p) => (
                  <Chip
                    key={p.key}
                    label={`+ ${p.label}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    onClick={() => insertPlaceholder(p.key)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
              <TextField
                label="Body Text"
                variant="outlined"
                fullWidth
                multiline
                rows={4}
                inputRef={bodyTextRef}
                value={form.body_text}
                onChange={(e) => setField('body_text', e.target.value)}
                sx={{ mb: 2 }}
                helperText="Click a chip above to insert a placeholder at the cursor"
              />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 1 }}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5, color: theme.palette.text.disabled }}>Font Size ({form.body_font_size}px)</Typography>
                  <Slider value={form.body_font_size} onChange={(_, v) => setField('body_font_size', v)} min={8} max={48} step={1} valueLabelDisplay="auto" />
                </Box>
                <ColorField label="Body Color" value={form.body_color} onChange={(e) => setField('body_color', e.target.value)} />
              </Box>

              {/* ── Signature ── */}
              <SectionHeader icon={<span>✍</span>}>Signature</SectionHeader>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 1 }}>
                <TextField label="Signature Label (e.g. Director)" variant="outlined" fullWidth value={form.signature_label} onChange={(e) => setField('signature_label', e.target.value)} />
                <UploadZone
                  label="Upload Signature Image"
                  preview={sigPrev}
                  onFile={(f) => handleFile('signature_image', f)}
                  onRemove={() => removeImage('signature_image')}
                  height={50}
                />
              </Box>

              {/* ── Stamp ── */}
              <SectionHeader icon={<span>🔩</span>}>Stamp</SectionHeader>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 1 }}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5, color: theme.palette.text.disabled }}>Stamp Position</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {STAMP_SLOTS.map((slot) => (
                      <SlotButton
                        key={slot.value}
                        active={form.stamp_position === slot.value}
                        onClick={() => setField('stamp_position', slot.value)}
                      >
                        {slot.label}
                      </SlotButton>
                    ))}
                  </Box>
                </Box>
                <UploadZone
                  label="Upload Stamp Image"
                  preview={stampPrev}
                  onFile={(f) => handleFile('stamp_image', f)}
                  onRemove={() => removeImage('stamp_image')}
                  height={50}
                />
              </Box>

              {/* ── Border ── */}
              <SectionHeader icon={<span>🖼️</span>}>Border</SectionHeader>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 1 }}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: theme.palette.text.primary }} id="border-style-label">Border Style</InputLabel>
                  <Select labelId="border-style-label" value={form.border_style} onChange={(e) => setField('border_style', e.target.value)} label="Border Style">
                    {BORDER_STYLES.map((v) => (<MenuItem key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</MenuItem>))}
                  </Select>
                </FormControl>
                <ColorField label="Border Color" value={form.border_color} onChange={(e) => setField('border_color', e.target.value)} />
              </Box>

              {/* ── Background ── */}
              <Box ref={bgSectionRef}>
                <SectionHeader icon={<span>🌈</span>}>Background</SectionHeader>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 1 }}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: theme.palette.text.primary }} id="bg-type-label">Background Type</InputLabel>
                    <Select labelId="bg-type-label" value={form.background_type} onChange={(e) => setField('background_type', e.target.value)} label="Background Type">
                      <MenuItem value="color">Color</MenuItem>
                      <MenuItem value="image">Image</MenuItem>
                    </Select>
                  </FormControl>
                  <ColorField label={isImageBg ? 'Background Color (shows around the image)' : 'Background Color'} value={form.background_color} onChange={(e) => setField('background_color', e.target.value)} />
                </Box>

                {isImageBg && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      borderRadius: 3,
                      border: `1px solid ${bgHasError ? theme.palette.error.main : theme.palette.divider}`,
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.015)',
                    }}
                  >
                    <UploadZone
                      label="Upload Background Image (JPG / PNG / WebP)"
                      preview={bgPrev}
                      onFile={(f) => handleFile('background_image', f)}
                      onRemove={() => removeImage('background_image')}
                      height={90}
                      wide
                      error={bgHasError}
                      helperText={bgHasError ? errors.background_image : `Recommended: ${Math.round((canvas.wMm / 25.4) * 300)} × ${Math.round((canvas.hMm / 25.4) * 300)}px (${pageInfo}). Max ${MAX_IMAGE_MB} MB.`}
                    />

                    {bgInfo && (
                      <Box sx={{ mt: 1.5 }}>
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
                          <Chip size="small" variant="outlined" label={`${bgDims.w} × ${bgDims.h}px`} />
                          <Chip size="small" variant="outlined" label={`Ratio ${bgInfo.imgAspect.toFixed(2)}:1 (page ${bgInfo.pageAspect.toFixed(2)}:1)`} />
                          <Chip size="small" color={bgInfo.quality.color} label={`~${bgInfo.dpi} dpi · ${bgInfo.quality.label}`} />
                        </Box>
                        {bgInfo.diff > 0.03 && form.background_fit === 'stretch' && (
                          <MuiAlert severity="warning" variant="outlined" sx={{ mb: 1 }}>
                            This image's shape differs from the page, so Stretch will distort it. Try "Fill" or "Fit".
                          </MuiAlert>
                        )}
                        {bgInfo.diff > 0.03 && form.background_fit !== 'stretch' && (
                          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1 }}>
                            Image shape differs from the page: "Fill" crops the edges, "Fit" leaves empty space filled with the background color.
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Fit mode */}
                    <Typography variant="body2" sx={{ mt: 2, mb: 0.75, fontWeight: 600 }}>Fit Mode</Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      {BG_FITS.map((f) => (
                        <Tooltip key={f.value} title={f.hint}>
                          <span>
                            <SlotButton active={form.background_fit === f.value} onClick={() => setField('background_fit', f.value)}>
                              {f.label}
                            </SlotButton>
                          </span>
                        </Tooltip>
                      ))}
                    </Box>

                    {/* Zoom / opacity */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 2 }}>
                      <Box>
                        <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
                          {form.background_fit === 'tile' ? 'Tile Size' : 'Zoom'} ({form.background_zoom}%)
                        </Typography>
                        <Slider value={Number(form.background_zoom)} onChange={(_, v) => setField('background_zoom', v)} min={25} max={300} step={1} valueLabelDisplay="auto" />
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>Opacity ({form.background_opacity}%)</Typography>
                        <Slider value={Number(form.background_opacity)} onChange={(_, v) => setField('background_opacity', v)} min={10} max={100} step={1} valueLabelDisplay="auto" />
                      </Box>
                    </Box>

                    {/* Position */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'auto 1fr 1fr' }, gap: 2, mt: 1, alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body2" sx={{ mb: 0.5, color: theme.palette.text.disabled }}>Anchor</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 22px)', gap: 0.5 }}>
                          {ANCHORS.map((yy) =>
                            ANCHORS.map((xx) => {
                              const active = Number(form.background_pos_x) === xx && Number(form.background_pos_y) === yy;
                              return (
                                <Box
                                  key={`${xx}-${yy}`}
                                  onClick={() => setForm((prev) => ({ ...prev, background_pos_x: xx, background_pos_y: yy }))}
                                  sx={{
                                    width: 22,
                                    height: 22,
                                    borderRadius: 0.75,
                                    cursor: 'pointer',
                                    border: `1px solid ${active ? theme.palette.primary.main : theme.palette.divider}`,
                                    backgroundColor: active ? theme.palette.primary.main : 'transparent',
                                    '&:hover': { borderColor: theme.palette.primary.main },
                                  }}
                                />
                              );
                            })
                          )}
                        </Box>
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>Position X ({form.background_pos_x}%)</Typography>
                        <Slider value={Number(form.background_pos_x)} onChange={(_, v) => setField('background_pos_x', v)} min={0} max={100} step={1} valueLabelDisplay="auto" />
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>Position Y ({form.background_pos_y}%)</Typography>
                        <Slider value={Number(form.background_pos_y)} onChange={(_, v) => setField('background_pos_y', v)} min={0} max={100} step={1} valueLabelDisplay="auto" />
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                      <Button size="small" variant="outlined" startIcon={<AutoAwesomeIcon />} disabled={!bgPrev} onClick={() => autoFitBackground(bgPrev, true)}>
                        Auto Fit
                      </Button>
                      <Button size="small" variant="outlined" startIcon={<TuneIcon />} onClick={resetBackgroundAdjustments}>
                        Reset Adjustments
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* ── Actions ── */}
              <Divider sx={{ mt: 3, mb: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                <Tooltip title="Preview with the server's sample data">
                  <span>
                    <Button
                      variant="outlined"
                      startIcon={previewing ? <CircularProgress size={16} /> : <PreviewIcon />}
                      onClick={handlePreview}
                      disabled={previewing}
                    >
                      PREVIEW
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Reset all design changes">
                  <Button variant="contained" color="error" onClick={handleReset} startIcon={<ResetIcon />}>RESET</Button>
                </Tooltip>
              </Box>
            </Paper>
          </Grid>

          {/* ══ RIGHT: Docked live preview ══ */}
          {docked && (
            <Grid item xs={12} md={6} lg={7}>
              <Box sx={{ position: { md: 'sticky' }, top: 16 }}>
                <Paper elevation={6} sx={{ borderRadius: '16px', overflow: 'hidden', border: `1px solid ${theme.palette.divider}` }}>
                  {renderPreviewHeader(false)}
                  {!previewCollapsed && (
                    <>
                      {renderPreviewToolbar(false)}
                      <Divider />
                      <Box
                        ref={dockViewportRef}
                        sx={{
                          p: 1.5,
                          overflow: autoFit ? 'hidden' : 'auto',
                          maxHeight: autoFit ? 'none' : 'calc(100vh - 220px)',
                          backgroundColor: theme.palette.action.hover,
                        }}
                      >
                        {renderCanvas(previewScale)}
                      </Box>
                      <Box sx={{ px: 1.5, py: 1 }}>
                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1 }}>
                          {pageInfo} · Sample data: John Doe • Sample Course • Batch A
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="caption" color="text.disabled" sx={{ alignSelf: 'center', mr: 1 }}>
                            Move by clicking:
                          </Typography>
                          {LOGO_SLOTS.map((slot) => (
                            <Chip
                              key={slot.value}
                              size="small"
                              label={`Logo → ${slot.label}`}
                              variant={form.logo_position === slot.value ? 'filled' : 'outlined'}
                              color="primary"
                              onClick={() => setField('logo_position', slot.value)}
                              sx={{ cursor: 'pointer' }}
                            />
                          ))}
                          {STAMP_SLOTS.map((slot) => (
                            <Chip
                              key={slot.value}
                              size="small"
                              label={`Stamp → ${slot.label}`}
                              variant={form.stamp_position === slot.value ? 'filled' : 'outlined'}
                              color="secondary"
                              onClick={() => setField('stamp_position', slot.value)}
                              sx={{ cursor: 'pointer' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    </>
                  )}
                </Paper>
              </Box>
            </Grid>
          )}
        </Grid>
      )}

      {/* ══ Floating live preview (draggable + resizable) ══ */}
      {!loading && previewMode === 'floating' && (
        <Box
          ref={floatPanelRef}
          sx={{
            position: 'fixed',
            left: floatPos.x,
            top: floatPos.y,
            zIndex: 1250,
            width: previewCollapsed ? 340 : Math.max(400, canvas.w * previewScale + 24),
            maxWidth: '96vw',
          }}
        >
          <Paper
            elevation={16}
            sx={{
              borderRadius: '14px',
              overflow: 'hidden',
              border: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              flexDirection: 'column',
              maxHeight: Math.max(200, window.innerHeight - floatPos.y - 8),
            }}
          >
            {renderPreviewHeader(true)}
            {!previewCollapsed && (
              <>
                {renderPreviewToolbar(true)}
                <Divider />
                <Box sx={{ p: 1.5, overflow: 'auto', flex: 1, minHeight: 0, backgroundColor: theme.palette.action.hover }}>
                  {renderCanvas(previewScale)}
                </Box>
                <Box sx={{ px: 1.5, py: 0.75 }}>
                  <Typography variant="caption" color="text.disabled">{pageInfo} · drag the header to move, drag the corner to resize</Typography>
                </Box>
              </>
            )}
          </Paper>
          {!previewCollapsed && (
            <Box
              onPointerDown={startResize}
              sx={{
                position: 'absolute',
                right: 2,
                bottom: 2,
                width: 18,
                height: 18,
                cursor: 'nwse-resize',
                touchAction: 'none',
                borderBottomRightRadius: 12,
                background: `linear-gradient(135deg, transparent 50%, ${theme.palette.primary.main} 50%)`,
              }}
            />
          )}
        </Box>
      )}

      {/* ── Snackbar ── */}
      <Snackbar open={openSnackbar} autoHideDuration={4000} onClose={() => setOpenSnackbar(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* ── Server Preview Modal ── */}
      {/* POST /api/admission/certificate-template/preview/ — server replaces placeholders with dummy data */}
      <Modal open={showPreviewModal} onClose={() => setShowPreviewModal(false)} BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.85)' } }}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: { xs: '95vw', md: 'min(1000px, 92vw)' }, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 24, overflow: 'hidden', maxHeight: '92vh' }}>
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>Certificate Preview · {pageInfo}</Typography>
            <IconButton onClick={() => setShowPreviewModal(false)} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
          </Box>
          <CustomPreviewScrollbar>
            <Box sx={{ p: 3 }}>
              {previewData ? (
                <ScaledCanvas canvasW={canvas.w} canvasH={canvas.h} fit>
                  <CertificateCanvas
                    form={form}
                    canvasW={canvas.w}
                    canvasH={canvas.h}
                    logoSrc={logoPrev}
                    sigSrc={sigPrev}
                    stampSrc={stampPrev}
                    bgSrc={bgPrev}
                    sample={serverSample}
                    overrides={{
                      institute_name: previewData.institute_name,
                      certificate_title: previewData.certificate_title,
                      body_text: previewData.body_text,
                    }}
                    showHints={false}
                  />
                </ScaledCanvas>
              ) : (
                <Typography>Loading preview...</Typography>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button variant="outlined" onClick={() => setShowPreviewModal(false)}>Close</Button>
              </Box>
            </Box>
          </CustomPreviewScrollbar>
        </Box>
      </Modal>

      {/* ── Overwrite Confirmation Modal (add mode only) ── */}
      {/* Shown when POST returns { success: false, overwrite_required: true, existing_template_id, message } */}
      {!isEdit && (
        <Modal open={showOverwriteModal} onClose={() => setShowOverwriteModal(false)} BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.85)' } }}>
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 440, maxWidth: '95vw', bgcolor: 'background.paper', borderRadius: 2, boxShadow: 24, overflow: 'hidden' }}>
            <Box sx={{ backgroundColor: theme.palette.warning.main, padding: '12px 16px' }}>
              <Typography variant="h6" sx={{ color: '#fff' }}>Template Already Exists ⚠️</Typography>
            </Box>
            <Box sx={{ padding: '20px' }}>
              <Typography sx={{ mb: 2 }}>
                {overwriteInfo?.message || 'A template already exists. Do you want to overwrite it?'}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 1 }}>
                <Button onClick={() => setShowOverwriteModal(false)} variant="outlined" data-app-cancel="true">Cancel</Button>
                <Button variant="contained" color="warning" onClick={handleOverwriteConfirm}>Yes, Overwrite</Button>
              </Box>
            </Box>
          </Box>
        </Modal>
      )}
    </Container>
  );
};

/* ─── Route component: /add-certificate ──────────────────────────────────── */
export default function AddCertificate() {
  return <CertificateDesigner mode="add" />;
}